import React, { useCallback, useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { paintDiameterSharedState } from './state';
import { DisplayConfig, Maybe, UnitSystemEnum } from '@/__generated__/graphql';
import { INCHES_TO_METERS_FACTOR, MILLIMETERS_TO_METERS_FACTOR } from '@/utils/unitSystem';
import { useGetAssemblyPipeSpecLazyQuery } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { formatFieldValue, getPipeSize } from '@/utils';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { METERS_TO_INCHES_FACTOR } from '@fabric-web/3d-viewer';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useUnitConversion } from '@/hooks/useUnitConversion';

export type Props = {
  onFieldValueChange?: (value: string) => void;
  title?: string;
  displayConfig?: Maybe<DisplayConfig>;
  value?: string;
  disableField?: boolean;
};

export const ProposedPaintDiameter = ({
  title,
  displayConfig,
  onFieldValueChange,
  value,
  disableField,
}: Props) => {
  const [paintDiameter, setPaintDiameter] = useAtom(paintDiameterSharedState);
  const rows = displayConfig?.textAreaRows || 4;
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const unitSystem = useAtomValue(state.unitSystem);
  const structurePipesByStructureId = useAtomValue(state.structurePipesByStructureId);
  const structureId = useGetStructureId();
  const { getUnitSuffix } = useUnitConversion();

  // Local state to manage the display value in the text field
  const [displayValue, setDisplayValue] = useState<string>(
    formatFieldValue({
      unitSystem,
      value: value
        ? String(Number(value) * METERS_TO_INCHES_FACTOR)
        : (String(Number(paintDiameter) * METERS_TO_INCHES_FACTOR) ?? NOT_APPLICABLE_LABEL),
      roundToDecimal: 2,
      showUnitLabel: false,
      type: undefined,
    }) ?? NOT_APPLICABLE_LABEL
  );
  const convertedValue = getPipeSize({
    size: Number(displayValue),
    unitSystem,
    structureSteelPipes: structurePipesByStructureId[structureId],
    getUnitSuffix,
    displayUnit: false,
  });

  const [getAssemblyPipeSpecLazyQuery] = useGetAssemblyPipeSpecLazyQuery();
  const handlePaintDiameter = useCallback(
    (value: string) => {
      let diameterInMeters;
      if (structurePipesByStructureId && unitSystem === UnitSystemEnum.Metric) {
        const standardMilimeter = getPipeSize({
          size: Number(value),
          unitSystem,
          structureSteelPipes: structurePipesByStructureId[structureId],
          getUnitSuffix,
          displayUnit: false,
        });
        diameterInMeters = String(Number(standardMilimeter) * MILLIMETERS_TO_METERS_FACTOR);
      } else {
        diameterInMeters = String(Number(value) * INCHES_TO_METERS_FACTOR);
      }
      setPaintDiameter(diameterInMeters);
      setDisplayValue(value);
      if (onFieldValueChange !== undefined) {
        onFieldValueChange(diameterInMeters);
      }
    },
    [
      getUnitSuffix,
      onFieldValueChange,
      setPaintDiameter,
      structureId,
      structurePipesByStructureId,
      unitSystem,
    ]
  );

  useEffect(() => {
    if (selectedAssemblyId) {
      getAssemblyPipeSpecLazyQuery({
        variables: {
          assemblyId: selectedAssemblyId,
        },
      }).then((response) => {
        const size = response.data?.assembly?.pipeSpec?.size;
        if (size) {
          setPaintDiameter(size);
          handlePaintDiameter(size);
        }
      });
    }
  }, [selectedAssemblyId]);
  return (
    <>
      <Box
        sx={{
          width: '100%',
          '& .MuiTextField-root': { width: '100%' },
        }}
        component="form"
        noValidate
        autoComplete="off"
      >
        <div>
          <TextField
            id={`TextArea-${title}`}
            label={`${title} (${unitSystem === UnitSystemEnum.Imperial ? 'in' : 'mm'})`}
            multiline={false}
            rows={rows}
            disabled={disableField}
            value={isNaN(Number(displayValue)) ? 0 : convertedValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              handlePaintDiameter(event.target.value);
            }}
          />
        </div>
      </Box>
    </>
  );
};
